import type { UploadedPreviewImages } from '@shared/components/PreviewWithPhoto/PreviewWithPhoto.types';
import { usePreviewWithPhotoState } from '@apps/admin/components/PreviewWithPhoto/usePreviewWithPhotoState';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export const CARD_PREVIEW_IMAGE_KEY = 'cardPreviewImage';
const CARD_PREVIEW_IMAGE_CLEARED_KEY = 'cardPreviewImageCleared';

/**
 * Preview-with-photo state for the card gallery. The gallery is opt-in by design: thumbnails keep
 * their stock photos until the couple picks images through the CTA, so nothing is seeded from the
 * event album here. (The website designer still auto-seeds — see `useUploadedPreviewImage`.)
 */
export const useCardPreviewWithPhoto = ({ enabled }: { enabled?: boolean } = {}) => {
  const { buttonInteracted } = useCardTelemetry();

  const handleCTAClick = useEventCallback((hasExisting: boolean) => {
    buttonInteracted({ name: hasExisting ? 'cardGallery_changePreviewPhotos' : 'cardGallery_viewWithYourPhoto' });
  });

  const handleClearAllPhotos = useEventCallback(() => {
    buttonInteracted({ name: 'cardGallery_clearPreviewPhotos' });
  });

  const handlePhotoSelectTelemetry = useEventCallback((photos: UploadedPreviewImages) => {
    buttonInteracted({ name: 'cardGallery_selectPhotosForPreview', count: photos.length });
  });

  return usePreviewWithPhotoState({
    storageKey: CARD_PREVIEW_IMAGE_KEY,
    clearedStorageKey: CARD_PREVIEW_IMAGE_CLEARED_KEY,
    enabled,
    onCTAClick: handleCTAClick,
    onClearAllPhotos: handleClearAllPhotos,
    onPhotoSelect: handlePhotoSelectTelemetry,
  });
};
